/**
 * @file device-push-token.schema.ts
 * @description Token de push de um device.
 *
 * Um documento por device (não por usuária): a mesma pessoa pode ter celular e
 * tablet, e a mesma pessoa pode trocar de conta no mesmo aparelho. `token` é a
 * chave natural — o FCM o rotaciona, e o registro reenviado atualiza `userId`
 * em vez de criar duplicata.
 */

import { z } from 'zod';

export const DevicePlatformEnum = z.enum(['ios', 'android', 'web']);
export type DevicePlatform = z.infer<typeof DevicePlatformEnum>;

export const DevicePushTokenSchema = z.object({
  id: z.string(),
  /** Token do FCM (ou Expo push token, quando via serviço da Expo). */
  token: z.string().min(1),
  userId: z.string(),
  platform: DevicePlatformEnum,
  /** Identificador estável do aparelho, quando disponível. Ajuda a limpar duplicatas. */
  deviceId: z.string().nullable().default(null),
  appVersion: z.string().nullable().default(null),
  /**
   * Desligado pela própria usuária nas configurações do app. Diferente de
   * inválido: o token continua bom, ela só não quer receber.
   */
  enabled: z.boolean().default(true),
  /**
   * Marcado quando o FCM devolve `registration-token-not-registered`. Não
   * apagamos: manter o registro evita reprocessar o mesmo token morto a cada
   * ciclo e dá rastro de quando parou.
   */
  invalidatedAt: z.coerce.date().nullable().default(null),
  lastUsedAt: z.coerce.date().nullable().default(null),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});

export type DevicePushToken = z.infer<typeof DevicePushTokenSchema>;
